/* istanbul ignore file */
import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  MenuItem,
  Box,
  Typography,
  FormControl,
  Grid,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { toast } from 'react-toastify';
import Divider from '@material-ui/core/Divider';
import ClearOutlinedIcon from '@material-ui/icons/ClearOutlined';
import { get } from 'lodash';
import { DrawerContainer } from 'containers/IntervenantManagement/styles';
import SecondaryButton from 'components/Buttons/SecondaryButton';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import { BUTTON_COLORS, COLORS } from 'constants/design';
import { formatPayloadToAdd } from 'containers/IntervenantManagement/utils';
import messages from 'containers/IntervenantManagement/messages';
import { IntlShape, injectIntl } from 'react-intl';
import { translate } from 'containers/services/translate';

interface LawyerOption {
  ItemKey: string;
  Name: string;
  Address?: string;
}

interface DateRange {
  start: number;
  end: number;
}

interface DrawerAddMappingProps {
  listings: any;
  isDrawerOpen: any;
  setIsDrawerOpen: Function;
  selectedRow: any;
  intl: IntlShape;
  requestAddMappingLawyers: Function;
  requestGetMappingLawyer: Function;
  lawyers: any;
  lawyersMapping: any;
}

const DrawerAddMapping: React.FC<DrawerAddMappingProps> = props => {
  const [selectedLawyer, setSelectedLawyer] = useState<LawyerOption | null>(
    null,
  );
  const [jurisdictionId, setJurisdictionId] = useState(null);
  const [stackHolders, setStackHolders] = React.useState([]);
  const [submitAttempted] = useState(false);
  const [ranges, setRanges] = useState<DateRange[]>([]);
  const [currentSelection, setCurrentSelection] = useState<{
    start: number | null;
    end: number | null;
  }>({
    start: null,
    end: null,
  });

  useEffect(() => {
    const assignedLawyers = props.lawyersMapping.map(
      (el: any) => el.lawyerCode,
    );
    setStackHolders(
      props.lawyers.filter(
        (item: any) => !assignedLawyers.includes(item.ItemKey),
      ),
    );
  }, [props.lawyersMapping, props.lawyers]);

  const handleDayClick = (day: number) => {
    // If we're not currently selecting a range, start a new one
    if (currentSelection.start === null) {
      setCurrentSelection({
        start: day,
        end: null,
      });
    }
    // If we've already started a range but haven't completed it
    else if (currentSelection.end === null) {
      // If clicked on the same day, make it a single-day selection
      if (day === currentSelection.start) {
        setRanges([...ranges, { start: day, end: day }]);
        setCurrentSelection({ start: null, end: null });
      }
      // If clicked on a day before the start, swap them
      else if (day < currentSelection.start) {
        setRanges([...ranges, { start: day, end: currentSelection.start }]);
        setCurrentSelection({ start: null, end: null });
      }
      // Otherwise, complete the range normally
      else {
        setRanges([...ranges, { start: currentSelection.start, end: day }]);
        setCurrentSelection({ start: null, end: null });
      }
    }
  };

  const isSelected = (day: number) => {
    // Check if day is in any of the completed ranges
    const inCompletedRanges = ranges.some(
      range => day >= range.start && day <= range.end,
    );

    // Check if day is in the current selection
    const inCurrentSelection =
      currentSelection.start !== null &&
      currentSelection.end === null &&
      day === currentSelection.start;

    return inCompletedRanges || inCurrentSelection;
  };

  const getRangeStyle = (day: number) => {
    // Find if this day is in any range
    for (const range of ranges) {
      if (day >= range.start && day <= range.end) {
        if (day === range.start && day === range.end) {
          return 'single'; // Single day selection
        } else if (day === range.start) {
          return 'start'; // Start of range
        } else if (day === range.end) {
          return 'end'; // End of range
        } else {
          return 'middle'; // Middle of range
        }
      }
    }

    // Check current selection
    if (currentSelection.start === day && currentSelection.end === null) {
      return 'single'; // Currently selecting
    }

    return 'none'; // Not selected
  };

  const handleRemoveRange = (rangeIndex: number) => {
    setRanges(ranges.filter((_, index) => index !== rangeIndex));
  };
  const handleResetState = () => {
    setJurisdictionId(null);
    setRanges([]);
  };
  const successCallBack = () => {
    toast.success(translate(messages.successUpdateTitle, props));
    props.setIsDrawerOpen(!props.isDrawerOpen);
    handleResetState();
  };

  const handleValidateDrawer = () => {
    props.requestAddMappingLawyers(
      formatPayloadToAdd(ranges, jurisdictionId, selectedLawyer),
      successCallBack,
    );
  };

  const renderCalendar = () => {
    const rows = Math.ceil(31 / 7);
    const calendar = [];

    for (let row = 0; row < rows; row++) {
      const days = [];
      for (let col = 0; col < 7; col++) {
        const dayNumber = row * 7 + col + 1;
        if (dayNumber <= 31) {
          const selected = isSelected(dayNumber);
          const rangeStyle = getRangeStyle(dayNumber);

          days.push(
            <Box
              key={dayNumber}
              style={{
                margin: '4px',
              }}
            >
              <Box
                onClick={() => handleDayClick(dayNumber)}
                style={{
                  cursor: 'pointer',
                  borderRadius:
                    rangeStyle === 'single'
                      ? '16px'
                      : rangeStyle === 'start'
                      ? '16px 0 0 16px'
                      : rangeStyle === 'end'
                      ? '0 16px 16px 0'
                      : rangeStyle === 'middle'
                      ? '0'
                      : '0',
                  backgroundColor: selected
                    ? 'rgb(17, 115, 206)'
                    : 'transparent',
                  color: selected ? 'white' : 'black',
                  textAlign: 'center',
                  padding: 1,
                  width: '26px',
                  height: '26px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: '0.2s',
                }}
              >
                {dayNumber}
              </Box>
            </Box>,
          );
        } else {
          days.push(
            <Box
              key={`empty-${col}`}
              style={{
                width: '36px',
                height: '36px',
                margin: '4px',
                padding: 1,
              }}
            ></Box>,
          );
        }
      }
      calendar.push(
        <Box
          key={`row-${row}`}
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            marginBottom: 1,
          }}
        >
          {days}
        </Box>,
      );
    }
    return calendar;
  };

  const renderSelectedRanges = () => {
    if (ranges.length === 0) return null;

    return (
      <Box style={{ marginTop: 2, marginBottom: 2 }}>
        <Typography variant="subtitle1" style={{ marginBottom: 1 }}>
          {translate(messages.selectedIntervalWorkTitle, props)}
        </Typography>
        <Box>
          {ranges.map((range, index) => (
            <Box
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: '16px',
              }}
            >
              <Typography variant="body2">
                {range.start === range.end
                  ? `Jour ${range.start}`
                  : `Jours ${range.start}-${range.end}`}
              </Typography>
              <Button
                style={{ minWidth: '24px', marginLeft: 1 }}
                onClick={() => handleRemoveRange(index)}
              >
                ×
              </Button>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const getHelperText = (field: string) => {
    switch (field) {
      case 'lawyer':
        return !selectedLawyer
          ? 'Veuillez sélectionner un avocat'
          : translate(messages.LawyerInfoTitle, props);
      case 'jurisdiction':
        return !jurisdictionId ? 'Veuillez sélectionner une juridiction' : '';
      case 'days':
        return ranges.length === 0
          ? 'Veuillez sélectionner au moins un jour'
          : '';
      default:
        return '';
    }
  };
  const handleToggleDrawer = () => {
    props.setIsDrawerOpen(false);
  };
  const handleLawyerChange = (event: any, newValue: any | null) => {
    setSelectedLawyer(newValue);
  };
  return (
    <DrawerContainer
      anchor="right"
      open={props.isDrawerOpen}
      onClose={handleToggleDrawer}
    >
      <div className="titleConatainer">
        <span className="title">
          {translate(messages.titleUpdateDrawer, props)}
        </span>

        <ClearOutlinedIcon
          data-testid={'close-icon'}
          onClick={handleToggleDrawer}
        />
      </div>
      <Divider className="divider" />
      <div style={{ padding: 20, height: '100%', overflowX: 'auto' }}>
        <Grid container direction="column" spacing={2}>
          <Grid item xs>
            <FormControl fullWidth>
              <Autocomplete
                options={stackHolders}
                getOptionLabel={(option: any) => `${option.ItemDescription}`}
                onChange={handleLawyerChange}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Avocat"
                    placeholder="Sélectionnez un avocat"
                  />
                )}
                renderOption={option => (
                  <Box display="flex" flexDirection="column">
                    <strong>{option.ItemDescription}</strong>
                    <small style={{ color: '#666' }}>{option.address}</small>
                  </Box>
                )}
                style={{ marginBottom: 16 }}
              />
            </FormControl>
          </Grid>
        </Grid>

        <TextField
          select
          label="Choisir une juridiction"
          value={jurisdictionId}
          onChange={e => setJurisdictionId(e.target.value as any)}
          fullWidth
          error={submitAttempted && !jurisdictionId}
          helperText={getHelperText('jurisdiction')}
          style={{ marginBottom: 30, marginTop: 20 }}
        >
          {get(props.listings, 'jurisdiction', []).map((j: any) => (
            <MenuItem key={j.id} value={j.id}>
              {j.title}
            </MenuItem>
          ))}
        </TextField>
        <span style={{ padding: '10px 0px' }}>
          {translate(messages.selectIntervalWorkTitle, props)}
        </span>
        <Box
          style={{
            border:
              submitAttempted && ranges.length === 0
                ? '1px solid #d32f2f'
                : '1px solid #ccc',
            borderRadius: '8px',
            padding: '10px',
            marginBottom: 30,
            marginTop: 20,
          }}
        >
          {renderCalendar()}
          {submitAttempted && ranges.length === 0 && (
            <Typography>{getHelperText('days')}</Typography>
          )}
        </Box>

        {renderSelectedRanges()}
      </div>
      <div className="actions">
        <Divider className="divider" />
        <div className="action-buttons-container">
          <SecondaryButton
            className="cancelButton"
            onClick={() => props.setIsDrawerOpen(!props.isDrawerOpen)}
            title={translate(messages.cancelButtonTitle, props)}
          />

          <PrimaryButton
            data-testid="primary-button"
            variant="contained"
            className={'saveButton'}
            onClick={handleValidateDrawer}
            title={translate(messages.addButtonTitle, props)}
            disabled={false}
            background={BUTTON_COLORS.PRIMARY_BLUE}
            border={BUTTON_COLORS.PRIMARY_BLUE}
            titlecolor={COLORS.WHITE}
          />
        </div>
      </div>
    </DrawerContainer>
  );
};

export default injectIntl(DrawerAddMapping);
